import { Button, Flex, Stack, Text, Tooltip } from '@chakra-ui/react';
import NiceModal from '@ebay/nice-modal-react';
import { isNil } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdArrowDownward } from 'react-icons/md';

import { IdentificationResultIcon } from '../../components/Icons';
import AlertModal from '../../components/modals/AlertModal';
import { DataTable } from '../../components/Table/DataTable';
import { ContentCard } from '../../layout/ContentCard';

const DocumentsCard = (props: {
  isLoadingParent: boolean;
  documents: Array<Document> | undefined;
  download(data: any): void;
  uploadMutation(data: any): Promise<any>;
  remove(id: number | boolean): Promise<any>;
}) => {
  const { t } = useTranslation();
  const { documents = [], download, isLoadingParent, remove } = props;

  const columns = useMemo(
    () => [
      {
        Header: t('documents:type.label'),
        accessor: 'type',
      },
      {
        Header: t('documents:comment.label'),
        accessor: 'comment',
      },
      {
        id: 'download',
        accessor: 'file',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          return (
            <Flex alignContent={'flex-end'}>
              <Button ml={'auto'} leftIcon={<MdArrowDownward />} variant="primary" size="sm" my="2" onClick={() => download(row.original)}>
                {t('common:download')}
              </Button>
            </Flex>
          );
        },
      },
      {
        id: 'button',
        accessor: 'id',
        // @ts-ignore
        Cell: (cellProps) => {
          const { row } = cellProps;
          const shouldBeDisabled = !isNil(row.original.workshop_user_id);
          const button = (
            <Flex alignContent={'flex-end'}>
              <Button
                ml={'auto'}
                variant="outline"
                size={'sm'}
                color={'accent'}
                onClick={() => {
                  NiceModal.show(AlertModal, {
                    onSubmit: () => remove(row.original.id),
                    children: t('documents:modal.delete.description'),
                    content: {
                      header: t('documents:modal.delete.header'),
                      footer: {
                        buttons: {
                          cancelCaption: t('common:cancel'),
                          actionCaption: t('common:delete'),
                        },
                      },
                    },
                  });
                }}
                disabled={shouldBeDisabled}
                data-test-id={'delete-' + row.index}
              >
                {t('common:delete')}
              </Button>
            </Flex>
          );

          return shouldBeDisabled ? (
            <Tooltip whiteSpace={'pre-wrap'} label={t('documents:delete.disabled')}>
              <span>{button}</span>
            </Tooltip>
          ) : (
            button
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documents],
  );

  return (
    <ContentCard
      isLoading={isLoadingParent}
      icon={<IdentificationResultIcon boxSize={10} />}
      minH="fit-content"
      header={t('documents:card.header')}
      contentDescription={t('documents:card.description')}
      data-test-id="documents-card"
    >
      <Stack direction={['column', 'row']} w={'100%'}>
        <Stack direction="column" w={'100%'} spacing={{ base: 4, lg: 6 }}>
          <Stack direction="column" w={'100%'}>
            {/*<FileForm isLoadingParent={isLoadingParent} uploadMutation={uploadMutation} />*/}
            {documents.length === 0 ? (
              <Text data-test-id="no-documents">{t('documents:card.no_documents')}</Text>
            ) : (
              <>
                <DataTable
                  data-test-id="documents-table"
                  data={documents}
                  columns={columns}
                  isMoreThenOnePage={false}
                  hasFilters={false}
                  hasSearchInput={undefined as any}
                />
              </>
            )}
          </Stack>
        </Stack>
      </Stack>
    </ContentCard>
  );
};

export default DocumentsCard;
